import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import { useCatchResponse } from '../../utils/useCatchResponse';
import { useReactToPrint } from "react-to-print";
import moment from 'moment';
import './FeeReceipt.css';

const FeeReceiptPrint = ({ paymentId, schoolId, onClose }) => {
    const { catchResponse } = useCatchResponse();
    const [receiptData, setReceiptData] = useState(null);
    const [template, setTemplate] = useState(null);
    const [templateList, setTemplateList] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [loading, setLoading] = useState(true);
    const elementRef = useRef(null);

    const printFn = useReactToPrint({
        contentRef: elementRef,
        documentTitle: "FeeReceipt",
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const loginData = CommonConfig.loginData();
            const userId = loginData?._id;

            const [receiptRes, templateRes] = await Promise.all([
                api.post(APIConstant.path.GetFeeReceipt, { PaymentId: paymentId }),
                api.post(APIConstant.path.GetFeeTemplateList, { UserID: schoolId || userId }),
            ]);

            if (receiptRes.success) {
                setReceiptData(receiptRes.data);
            }
            if (templateRes.success && templateRes.data?.length > 0) {
                const templates = templateRes.data.map(t => ({ label: t.TemplateName, value: t._id, ...t }));
                setTemplateList(templates);
                // Use the template saved with the payment, or fall back to first
                const savedTemplateId = receiptRes.data?.Payment?.FeeTemplateId;
                const defaultTemplate = savedTemplateId
                    ? templates.find(t => t.value === savedTemplateId) || templates[0]
                    : templates[0];
                setTemplate(defaultTemplate);
                setSelectedTemplateId(defaultTemplate._id || defaultTemplate.value);
            }
            setLoading(false);
            Loader.hide();
        } catch (err) {
            setLoading(false);
            Loader.hide();
            catchResponse(err);
        }
    };

    const handleTemplateChange = (templateId) => {
        setSelectedTemplateId(templateId);
        const selected = templateList.find(t => t.value === templateId);
        if (selected) setTemplate(selected);
    };

    const generatePDF = () => {
        try {
            printFn();
        } catch (error) {
            console.log('Error in generate PDF:', error);
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>Loading receipt...</div>;
    if (!receiptData) return <div style={{ textAlign: 'center', padding: '40px' }}>Receipt not found</div>;

    const { Payment, StudentInfo, SchoolInfo, CollectedByName } = receiptData;
    const school = SchoolInfo || {};
    const t = template || {};
    const showField = (field, defaultVal = true) => t[field] !== undefined ? t[field] : defaultVal;
    const headerBG = t.HeaderBGColor || '#1C515E';

    const subTotal = Payment.TotalAmount || 0;
    const discountAmount = Payment.DiscountAmount || 0;
    const netAmount = Payment.NetAmount || 0;

    return (
        <div>
            {templateList.length > 0 && (
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontWeight: 600, fontSize: '13px' }}>Select Template:</label>
                    <Dropdown
                        value={selectedTemplateId}
                        options={templateList}
                        onChange={(e) => handleTemplateChange(e.value)}
                        placeholder="Select Template"
                        style={{ minWidth: '200px' }}
                    />
                </div>
            )}

            {/* Visible Preview */}
            <div className="fee-receipt-preview">
                <ReceiptContent
                    Payment={Payment}
                    StudentInfo={StudentInfo}
                    school={school}
                    CollectedByName={CollectedByName}
                    t={t}
                    showField={showField}
                    headerBG={headerBG}
                    subTotal={subTotal}
                    discountAmount={discountAmount}
                    netAmount={netAmount}
                />
            </div>

            {/* Hidden print content */}
            <div className="p-d-none">
                <div ref={elementRef} id="pdf-receipt-content" className="fee-receipt-print-content">
                    <ReceiptContent
                        Payment={Payment}
                        StudentInfo={StudentInfo}
                        school={school}
                        CollectedByName={CollectedByName}
                        t={t}
                        showField={showField}
                        headerBG={headerBG}
                        subTotal={subTotal}
                        discountAmount={discountAmount}
                        netAmount={netAmount}
                    />
                </div>
            </div>

            <div className="dialog-footer p-d-flex p-ai-center p-jc-between p-mt-3">
                <div className='p-d-flex p-ai-center'>
                    <Button icon="pi pi-file-pdf" rounded className="e-border-btn p-m-0" tooltip="Generate PDF" tooltipOptions={{ className: 'bluegray-tooltip text-13', position: 'top' }} onClick={generatePDF} />
                </div>
                <div className='p-d-flex p-ai-center p-jc-end'>
                    <Button className='btn-text-action-secondary' label="Close" onClick={onClose} />
                </div>
            </div>
        </div>
    );
};

// Separate Receipt Content component for reuse in preview and print
const ReceiptContent = ({ Payment, StudentInfo, school, CollectedByName, t, showField, headerBG, subTotal, discountAmount, netAmount }) => {
    return (
        <div className="fee-receipt-container" style={{ fontFamily: t.FontTheme || 'Arial, sans-serif' }}>
            <div className="fee-receipt-border" style={{ border: `2px solid ${headerBG}` }}>
                {/* Header */}
                <div className="fee-receipt-header" style={{ borderBottom: `2px solid ${headerBG}` }}>
                    {showField('ShowSchoolLogo') && (school.Logo || t.SchoolImage) && (
                        <img
                            src={school.Logo ? `${apiBase}/UserProfilePic/${school.Logo}` : `${apiBase}/Upload/FeeTemplate/${t.SchoolImage}`}
                            alt="logo"
                            className="fee-receipt-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                    {showField('ShowSchoolName') && (
                        <h2 className="fee-receipt-school-name" style={{ color: headerBG }}>{school.SchoolName || 'School'}</h2>
                    )}
                    {school.Slogan && (
                        <p className="fee-receipt-slogan">{school.Slogan}</p>
                    )}
                    {showField('ShowSchoolAddress') && school.Address && (
                        <p className="fee-receipt-address">{school.Address}</p>
                    )}
                    {showField('ShowSchoolAddress') && (school.Phone || school.Email) && (
                        <p className="fee-receipt-contact">
                            {[school.Phone, school.Email].filter(Boolean).join(' | ')}
                        </p>
                    )}
                    <h1 className="fee-receipt-title" style={{ color: headerBG }}>
                        {t.ReceiptTitle || 'FEE RECEIPT'}
                    </h1>
                </div>

                {/* Receipt Meta */}
                <div className="fee-receipt-meta">
                    <span>Receipt No: <strong style={{ color: headerBG }}>{Payment.ReceiptNo}</strong></span>
                    <span>Date: <strong style={{ color: headerBG }}>{moment(Payment.PaymentDate).format('DD/MM/YYYY')}</strong></span>
                    {showField('ShowPaymentMode') && Payment.PaymentMode && (
                        <span>Mode: <strong style={{ color: headerBG }}>{Payment.PaymentMode}</strong></span>
                    )}
                </div>

                {/* Student Info */}
                <div className="fee-receipt-student-info">
                    <div className="fee-receipt-student-left">
                        {showField('ShowStudentPhoto') && (
                            <div className="fee-receipt-photo" style={{ borderColor: headerBG }}>
                                <img
                                    src={StudentInfo.ProfilePic ? `${apiBase}/UserProfilePic/${StudentInfo.ProfilePic}` : ''}
                                    alt=""
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </div>
                        )}
                        <div className="fee-receipt-student-details">
                            <div><span className="fee-receipt-label" style={{ color: headerBG }}>Student Name:</span> {StudentInfo.StudentName}</div>
                            {showField('ShowFatherName') && StudentInfo.FatherName && (
                                <div><span className="fee-receipt-label" style={{ color: headerBG }}>Father's Name:</span> {StudentInfo.FatherName}</div>
                            )}
                            {showField('ShowContactNo', false) && StudentInfo.ContactNo && (
                                <div><span className="fee-receipt-label" style={{ color: headerBG }}>Contact:</span> {StudentInfo.ContactNo}</div>
                            )}
                        </div>
                    </div>
                    <div className="fee-receipt-student-right">
                        {showField('ShowClass') && (
                            <div><span className="fee-receipt-label" style={{ color: headerBG }}>Class:</span> {StudentInfo.ClassName}</div>
                        )}
                        {showField('ShowBoard') && StudentInfo.BoardName && (
                            <div><span className="fee-receipt-label" style={{ color: headerBG }}>Board:</span> {StudentInfo.BoardName}</div>
                        )}
                        {showField('ShowRollNo') && (
                            <div><span className="fee-receipt-label" style={{ color: headerBG }}>Roll No:</span> {StudentInfo.RollNo}</div>
                        )}
                        {showField('ShowGRNo') && (
                            <div><span className="fee-receipt-label" style={{ color: headerBG }}>G.R. No:</span> {StudentInfo.GRNo}</div>
                        )}
                    </div>
                </div>

                {/* Fee Table */}
                <table className="fee-receipt-table">
                    <thead>
                        <tr>
                            <th style={{ background: headerBG }}>Sr No.</th>
                            <th style={{ background: headerBG }}>Particulars</th>
                            <th style={{ background: headerBG, textAlign: 'right' }}>Fine</th>
                            <th style={{ background: headerBG, textAlign: 'right' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Payment.PaymentItems?.map((item, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{item.FeeTypeName}</td>
                                <td style={{ textAlign: 'right' }}>&#8377; {(item.Fine || 0).toLocaleString('en-IN')}</td>
                                <td style={{ textAlign: 'right' }}>&#8377; {((item.Amount || 0) + (item.Fine || 0)).toLocaleString('en-IN')}</td>
                            </tr>
                        ))}
                        <tr className="fee-receipt-subtotal-row">
                            <td colSpan="3" style={{ textAlign: 'right' }}>Sub Total</td>
                            <td style={{ textAlign: 'right' }}>&#8377; {subTotal.toLocaleString('en-IN')}</td>
                        </tr>
                        {discountAmount > 0 && (
                            <tr className="fee-receipt-discount-row">
                                <td colSpan="3" style={{ textAlign: 'right' }}>Discount ({Payment.DiscountPercent}%)</td>
                                <td style={{ textAlign: 'right' }}>- &#8377; {discountAmount.toLocaleString('en-IN')}</td>
                            </tr>
                        )}
                        <tr className="fee-receipt-total-row">
                            <td colSpan="3" style={{ textAlign: 'right', borderTopColor: headerBG }}>TOTAL</td>
                            <td style={{ textAlign: 'right', color: headerBG, borderTopColor: headerBG }}>&#8377; {netAmount.toLocaleString('en-IN')}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Remarks */}
                {showField('ShowRemarks') && Payment.Remarks && (
                    <div className="fee-receipt-remarks">
                        <span className="fee-receipt-label" style={{ color: headerBG }}>Remarks:</span> {Payment.Remarks}
                    </div>
                )}

                {/* Footer Signatures */}
                {showField('ShowSignature') && (
                    <div className="fee-receipt-footer">
                        <div className="fee-receipt-signature">
                            <div className="fee-receipt-signature-line">Student/Parent Signature</div>
                        </div>
                        <div className="fee-receipt-signature">
                            {t.SignImage && (
                                <img
                                    src={`${apiBase}/Upload/FeeTemplate/${t.SignImage}`}
                                    alt="sign"
                                    className="fee-receipt-sign-img"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            )}
                            <div className="fee-receipt-signature-line">
                                {showField('ShowCollectedBy') && CollectedByName ? `Collected By: ${CollectedByName}` : 'Authorized Signature'}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeeReceiptPrint;
